import { useState, useCallback, useMemo } from 'react';
import { getTokenWithOrgContext } from '../utils';
import { useAuthStore } from '../stores';
import { AUTH_REDIRECT_COMPLETED_FLAG } from '../constants';
import type {
    GetTokenSilentlyOptions,
    RedirectLoginOptions,
} from '@auth0/auth0-react';

/**
 * Result of useAuthOrganizationValidation hook
 */
export interface UseAuthOrganizationValidationResult {
    /** Whether token validation is in progress */
    isTokenValidationLoading: boolean;
    /** Whether we are about to redirect for token renewal (page refresh) */
    isRedirectingForTokenRenewal: boolean;
    /** Function to validate auth and org selection, redirecting if needed */
    validateAuthAndOrg: () => void;
}

/**
 * Parameters for useAuthOrganizationValidation hook
 */
export interface UseAuthOrganizationValidationParams {
    /** Auth0's getAccessTokenSilently function */
    getAccessTokenSilently: (
        options?: GetTokenSilentlyOptions,
    ) => Promise<string>;
    /** Auth0's loginWithRedirect function */
    loginWithRedirect: (options?: RedirectLoginOptions) => Promise<void>;
    /** Called when the session has expired and a full logout is required */
    onSessionExpired: () => void;
    /** Whether authentication is loading */
    isAuthLoading: boolean;
    /** Whether user is authenticated */
    isAuthenticated: boolean;
    /** Whether dynomite is loaded */
    isDynomiteLoaded: boolean;
    /** React Router navigate function */
    navigate: (path: string, options?: { replace?: boolean }) => void;
    /** Custom organization/select path. Defaults to '/organization/select'. */
    selectOrgPath?: string;
}

/**
 * Hook to handle organization selection and token validation.
 * Used by BaseLayout to centralize auth+org validation logic.
 *
 * Manages:
 * - Forced interactive token renewal on full page load/refresh
 *   (ensures fresh token on every page load for security)
 * - Token refresh with organization context
 * - Full logout (via onSessionExpired) when the token can no longer
 *   be obtained
 * - Redirect to organization/select when no org is selected
 * - Loading state
 *
 * Uses the Zustand auth store for organization ID persistence and a
 * sessionStorage flag (AUTH_REDIRECT_COMPLETED_FLAG) to detect whether
 * the user just completed an Auth0 redirect round-trip.
 *
 * @param params - Configuration parameters
 * @returns Object with validation state and trigger function
 */
export const useAuthOrganizationValidation = (
    params: UseAuthOrganizationValidationParams,
): UseAuthOrganizationValidationResult => {
    const {
        getAccessTokenSilently,
        loginWithRedirect,
        onSessionExpired,
        isAuthLoading,
        isAuthenticated,
        isDynomiteLoaded,
        navigate,
        selectOrgPath = '/organization/select',
    } = params;

    // Get selectedOrgId from Zustand store
    const selectedOrgId = useAuthStore((state) => state.selectedOrgId);

    const [isTokenValidationLoading, setTokenValidationLoading] =
        useState(false);

    // Synchronously detect if we are in a page refresh that requires
    // token renewal. This allows BaseLayout to show a loader instead
    // of flashing the page content before the Auth0 redirect occurs.
    const isRedirectingForTokenRenewal = useMemo(() => {
        // If auth is still loading, we can't determine the state yet
        if (isAuthLoading) {
            return false;
        }

        // If not authenticated, no renewal needed
        if (!isAuthenticated) {
            return false;
        }

        // If no org selected, no renewal needed
        if (!selectedOrgId) {
            return false;
        }

        // If we just completed an Auth0 redirect, no renewal needed
        const justCompletedAuth = sessionStorage.getItem(
            AUTH_REDIRECT_COMPLETED_FLAG,
        );
        if (justCompletedAuth) {
            return false;
        }

        // This is a page refresh with an org selected:
        // we will redirect to Auth0 for token renewal
        return true;
    }, [isAuthLoading, isAuthenticated, selectedOrgId]);

    const refreshTokenWithOrg = useCallback(async (): Promise<
        string | null
    > => {
        if (!selectedOrgId) {
            return null;
        }

        try {
            setTokenValidationLoading(true);

            return await getTokenWithOrgContext(getAccessTokenSilently);
        } catch {
            // The token can no longer be obtained: the session is
            // unusable. Perform a full logout; the user will land on '/'
            // and ProtectedRoute will trigger a fresh login.
            onSessionExpired();
            return null;
        } finally {
            setTokenValidationLoading(false);
        }
    }, [getAccessTokenSilently, selectedOrgId, onSessionExpired]);

    const validateAuthAndOrg = useCallback(() => {
        const validate = async (): Promise<void> => {
            if (isAuthLoading || !isDynomiteLoaded) {
                return;
            }

            if (!isAuthenticated) {
                return;
            }

            const currentPath = window.location.pathname;
            const justCompletedAuth = sessionStorage.getItem(
                AUTH_REDIRECT_COMPLETED_FLAG,
            );

            if (justCompletedAuth) {
                // The user just came back from an Auth0 redirect:
                // consume the flag and continue with normal validation.
                sessionStorage.removeItem(AUTH_REDIRECT_COMPLETED_FLAG);
            } else if (selectedOrgId) {
                // Full page load (e.g. refresh) with an organization
                // already selected: force an interactive token renewal.
                // This ensures a fresh token on every page load for security.
                // We pass the current path in appState so the user is
                // redirected back to the same page after token renewal.
                loginWithRedirect({
                    authorizationParams: {
                        organization: selectedOrgId,
                    },
                    appState: {
                        returnTo:
                            window.location.pathname + window.location.search,
                    },
                });
                return;
            }

            if (!selectedOrgId) {
                if (!currentPath.includes(selectOrgPath)) {
                    navigate(selectOrgPath, { replace: true });
                }
                return;
            }

            await refreshTokenWithOrg();
        };

        validate();
    }, [
        isAuthLoading,
        isAuthenticated,
        isDynomiteLoaded,
        navigate,
        selectOrgPath,
        refreshTokenWithOrg,
        selectedOrgId,
        loginWithRedirect,
    ]);

    return {
        isTokenValidationLoading,
        isRedirectingForTokenRenewal,
        validateAuthAndOrg,
    };
};

export default useAuthOrganizationValidation;
